import { selectedEntityIds } from '../../../../shared/utils/entitySelection';
import useFormTableFields from '../../../../shared/data/useFormTableFields';
import { listAccountingCustomFields } from '../../services/accounting-custom-fields.service';
import { expenseFormSchema } from '../../config/accountingForm.schemas';
import React, { useEffect, useState } from 'react';
import DataTable from '../../../../shared/components/patterns/DataTable';
import ConfirmActionDialog from '../../../../shared/components/patterns/ConfirmActionDialog';
import AccountingAttachmentsPanel from '../shared/AccountingAttachmentsPanel.jsx';
import ExpenseDetailDialog from './ExpenseDetailDialog.jsx';
import { AccountingInlineActions, getExpensesTableColumns } from '../shared/accountingPage.config';

export default function ExpensesTable({
  expenses,
  staff = [],
  suppliers = [],
  onRegisterPayment,
  onEditExpense,
  onVoidExpense,
  canManageAccountingDestructiveActions = false,
  focusedEntityId = '',
  focusedMode = 'view',
  onClearFocus,
  views = [],
  activeViewId,
  onViewChange,
}) {
  const tableFields = useFormTableFields(expenseFormSchema.id, listAccountingCustomFields);
  const [registerRequest, setRegisterRequest] = useState(null);
  const [voidRequest, setVoidRequest] = useState(null);
  const [detailExpense, setDetailExpense] = useState(null);
  const [detailMode, setDetailMode] = useState('view');
  const currentDetailExpense =
    expenses.find((item) => String(item.id) === String(detailExpense?.id)) || detailExpense;
  const staffById = new Map(staff.map((member) => [String(member.id), member]));
  const suppliersById = new Map(suppliers.map((supplier) => [String(supplier.id), supplier]));
  useEffect(() => {
    if (!focusedEntityId) return;
    const target = expenses.find((item) => String(item.id) === String(focusedEntityId));
    if (target) {
      setDetailExpense(target);
      setDetailMode(focusedMode);
      if (focusedMode === 'delete' && canManageAccountingDestructiveActions) setVoidRequest(target);
    }
  }, [expenses, focusedEntityId, focusedMode, onEditExpense, canManageAccountingDestructiveActions]);

  function getRecipientLabel(expense) {
    if (expense.recipientType === 'STAFF' && selectedEntityIds(expense, 'staff').length > 1) return selectedEntityIds(expense, 'staff').map((id) => staffById.get(id)?.name || `Staff #${id}`).join('; ');
    if (expense.recipientType === 'STAFF') {
      const member = expense.staffId == null ? null : staffById.get(String(expense.staffId));
      return (
        member?.name ||
        (expense.staffId == null ? 'Staff non risolto' : `Staff non risolto #${expense.staffId}`)
      );
    }

    if (expense.recipientType === 'SUPPLIER') {
      const supplier = expense.supplierId == null ? null : suppliersById.get(String(expense.supplierId));
      return expense.supplierName || supplier?.displayName || supplier?.legalName || 'Fornitore non risolto';
    }

    return '-';
  }

  function getActions(expense) {
    const actions = [
      {
        id: 'preview',
        label: 'Anteprima uscita',
        iconKey: 'actions.preview',
        onClick: () => {
          setDetailMode('view');
          setDetailExpense(expense);
        },
      },
    ];

    if (expense.status !== 'pagato' && expense.status !== 'annullata') {
      actions.push({
        id: 'register',
        label: 'Registra pagamento',
        title: 'Registra pagamento',
        iconKey: 'actions.accept',
        tone: 'success',
        onClick: () => setRegisterRequest(expense),
      });
    }

    actions.push({
      id: 'edit',
      label: 'Modifica',
      title: 'Modifica uscita',
      iconKey: 'actions.edit',
      onClick: () => {
        setDetailMode('edit');
        setDetailExpense(expense);
      },
    });

    if (canManageAccountingDestructiveActions && expense.status !== 'annullata') {
      actions.push({
        id: 'void',
        label: 'Annulla',
        title: 'Annulla uscita',
        iconKey: 'actions.reject',
        tone: 'danger',
        onClick: () => setVoidRequest(expense),
      });
    }

    return actions;
  }

  return (
    <>
      <section className="accounting-table-section">
        <header className="accounting-section-heading">
          <h2>Registro Uscite</h2>
          <p>{expenses.length} uscite trovate</p>
        </header>
        <DataTable
          ariaLabel="Registro Uscite"
          formSchema={expenseFormSchema}
          views={
            views.length
              ? views
              : [
                  {
                    id: 'expenses',
                    label: 'Uscite',
                    iconKey: 'metrics.currency',
                    tone: 'danger',
                    count: expenses.length,
                  },
                ]
          }
          activeViewId={activeViewId}
          onViewChange={onViewChange}
          customFields={tableFields.definitions}
          error={tableFields.error}
          columns={[
            ...getExpensesTableColumns({ getRecipientLabel }),
            {
              id: 'actions',
              header: '',
              render: (expense) => {
                const recipientLabel = getRecipientLabel(expense);
                return (
                  <AccountingInlineActions
                    actions={getActions(expense)}
                    extra={
                      <AccountingAttachmentsPanel
                        entity={expense}
                        linkedEntityType="ACCOUNTING_EXPENSE"
                        entityLabel={expense.description || `spesa ${expense.id}`}
                        clientName={expense.supplierName || recipientLabel || 'Contabilita'}
                      />
                    }
                  />
                );
              },
            },
          ]}
          rows={expenses}
          getRowId={(expense) => expense.id}
          onRowClick={(expense) => {
            setDetailMode('view');
            setDetailExpense(expense);
          }}
          selectedRowId={detailExpense?.id}
          emptyState="Nessuna uscita trovata con i filtri selezionati"
        />
      </section>
      <ExpenseDetailDialog
        open={Boolean(detailExpense)}
        initialMode={detailMode}
        expense={currentDetailExpense}
        recipientLabel={currentDetailExpense ? getRecipientLabel(currentDetailExpense) : ''}
        onClose={() => {
          setDetailExpense(null);
          onClearFocus?.();
        }}
        focusComments={focusedMode === 'comments'}
        onRegisterPayment={(expense) => setRegisterRequest(expense)}
        onUpdateExpense={onEditExpense}
        staff={staff}
        suppliers={suppliers}
        onVoidExpense={(expense) => setVoidRequest(expense)}
        canManageAccountingDestructiveActions={canManageAccountingDestructiveActions}
      />
      <ConfirmActionDialog
        open={Boolean(registerRequest)}
        title="Registrare questo pagamento?"
        description={`Confermi la registrazione del pagamento "${registerRequest?.description || ''}"?`}
        confirmLabel="Registra pagamento"
        cancelLabel="Annulla"
        onCancel={() => setRegisterRequest(null)}
        onConfirm={async () => {
          await onRegisterPayment?.(registerRequest.id);
          setRegisterRequest(null);
        }}
      />
      <ConfirmActionDialog
        open={Boolean(voidRequest)}
        title="Annullare questa uscita?"
        description={`L'uscita "${voidRequest?.description || ''}" verrà annullata.`}
        confirmLabel="Annulla uscita"
        cancelLabel="Indietro"
        tone="danger"
        onCancel={() => setVoidRequest(null)}
        onConfirm={async () => {
          const result = await onVoidExpense?.(voidRequest.id);
          if (result?.ok) {
            setVoidRequest(null);
            setDetailExpense(null);
            onClearFocus?.();
          }
        }}
      />
    </>
  );
}
